import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ArrowForward from '@mui/icons-material/ArrowForward';
import VisibilityOutlined from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlined from '@mui/icons-material/VisibilityOffOutlined';
import type { SessionUser } from '@zilian/contracts';
import { BrandLogo } from '@zilian/ui';
import { Wordmark } from '@zilian/ui';
import { api } from '../api';

/**
 * The login page shows the product instead of describing it: a window plays one real
 * transaction between two enterprises, each keeping its own books — the order is confirmed
 * by both, the supplier's stock goes down as goods ship, the buyer's goes up on receipt, and
 * both agree the amount. Styles: styles.css, .login-*, .stage-*.
 */

const PHASES = ['下单', '双方确认', '发货', '验收入库', '对账结清'];
const PHASE_MS = 2800;
const reducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** A number that counts to its new value instead of jumping. */
function Rolling({ value }: { value: number }) {
  const [shown, setShown] = useState(value);
  const from = useRef(value);
  useEffect(() => {
    if (reducedMotion()) return setShown(value);
    const start = performance.now();
    const begin = from.current;
    let frame = 0;
    const tick = (t: number) => {
      const k = Math.min(1, (t - start) / 900);
      const eased = 1 - Math.pow(1 - k, 3);
      setShown(Math.round(begin + (value - begin) * eased));
      if (k < 1) frame = requestAnimationFrame(tick);
      else from.current = value;
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [value]);
  return <>{shown.toLocaleString('zh-CN')}</>;
}

function Ledger(p: {
  side: string;
  role: string;
  status: string;
  tone: 'wait' | 'ok' | 'move';
  stock: number;
  stockNote: string;
  active: boolean;
}) {
  return (
    <div className={`stage-card${p.active ? ' is-active' : ''}`}>
      <div className="stage-card-head">
        <span className="stage-card-side">{p.side}</span>
        <span className="stage-card-role">{p.role}</span>
      </div>
      <div className="stage-order">
        <div>
          <div className="stage-order-no">CO-1029</div>
          <div className="stage-order-item">500 ml 塑料瓶 × 300</div>
        </div>
        <span key={p.status} className={`stage-chip tone-${p.tone}`}>
          {p.status}
        </span>
      </div>
      <div className="stage-stock">
        <span className="stage-stock-label">本企业库存</span>
        <span className="stage-stock-value">
          <Rolling value={p.stock} />
          <small> 个</small>
        </span>
      </div>
      <div className="stage-stock-note">{p.stockNote}</div>
    </div>
  );
}

export function ProductStage() {
  const [phase, setPhase] = useState(() => (reducedMotion() ? 4 : 0));
  useEffect(() => {
    if (reducedMotion()) return;
    // Five steps, then a pause on the settled state before starting over.
    const timer = setInterval(() => setPhase((p) => (p + 1) % (PHASES.length + 1)), PHASE_MS);
    return () => clearInterval(timer);
  }, []);
  const step = Math.min(phase, PHASES.length - 1);
  const confirmed = step >= 1;
  const shipped = step >= 2;
  const received = step >= 3;
  const settled = step >= 4;
  // What travels between the two enterprises in this step, and which way.
  const flow =
    step === 0
      ? { label: '订单', dir: 'right' }
      : step === 2
        ? { label: '货物 300 个', dir: 'left' }
        : step === 4
          ? { label: '对账 ¥255.00', dir: 'both' }
          : null;
  return (
    <div className="stage" aria-hidden>
      <div className="stage-top">
        <span className="stage-title">一笔交易，两本账</span>
        <span className="stage-live">
          <i />
          实时同步
        </span>
      </div>
      <div className="stage-board">
        <Ledger
          side="我方"
          role="采购"
          status={
            settled
              ? '已结清'
              : received
                ? '已入库'
                : shipped
                  ? '运输中'
                  : confirmed
                    ? '已确认'
                    : '待对方确认'
          }
          tone={received || settled ? 'ok' : shipped ? 'move' : confirmed ? 'ok' : 'wait'}
          stock={received ? 700 : 400}
          stockNote={received ? '+300 合格入库' : '收货后入库'}
          active={step === 0 || step === 3}
        />
        <div className="stage-link">
          <div className="stage-link-line" />
          {flow && (
            <div key={`${step}-${phase}`} className={`stage-link-flow dir-${flow.dir}`}>
              <span className="stage-link-dot" />
              <span className="stage-link-label">{flow.label}</span>
            </div>
          )}
        </div>
        <Ledger
          side="合作方"
          role="销售"
          status={settled ? '已结清' : shipped ? '已发货' : confirmed ? '已确认' : '待确认'}
          tone={settled || confirmed ? 'ok' : 'wait'}
          stock={shipped ? 900 : 1200}
          stockNote={shipped ? '−300 已出库' : '确认后可发货'}
          active={step === 1 || step === 2}
        />
      </div>
      <div className={`stage-settle${settled ? ' is-on' : ''}`}>
        <span>对账金额</span>
        <b>¥ 255.00</b>
        <span className="stage-settle-ok">双方已确认</span>
      </div>
      <ol className="stage-steps">
        {PHASES.map((name, i) => (
          <li key={name} className={i < step ? 'is-done' : i === step ? 'is-now' : ''}>
            <span className="stage-step-bar">
              {i === step && phase < PHASES.length && (
                <span key={phase} className="stage-step-fill" />
              )}
            </span>
            {name}
          </li>
        ))}
      </ol>
    </div>
  );
}

export function Login({ onLogin }: { onLogin: (user: SessionUser) => void }) {
  const [username, setUsername] = useState(''),
    [password, setPassword] = useState(''),
    [reveal, setReveal] = useState(false),
    [error, setError] = useState(''),
    [busy, setBusy] = useState(false);
  return (
    <div className="login-page">
      <section className="login-side">
        <div className="login-brand rise" style={{ ['--d' as string]: '0ms' }}>
          <BrandLogo size={36} />
          <Wordmark height={24} />
        </div>

        <div className="login-main">
          <h1 className="login-headline rise" style={{ ['--d' as string]: '80ms' }}>
            每一次交付，
            <br />
            都有清晰的来处。
          </h1>
          <p className="login-lede rise" style={{ ['--d' as string]: '160ms' }}>
            各自记账，订单相连。确认、发货、验收、对账，交易双方看到的是同一笔账。
          </p>

          <Box
            component="form"
            className="rise"
            style={{ ['--d' as string]: '240ms' }}
            onSubmit={async (e) => {
              e.preventDefault();
              setBusy(true);
              setError('');
              try {
                onLogin(await api('/auth/login', { username, password, channel: 'pc' }));
              } catch (err) {
                setError((err as Error).message);
              } finally {
                setBusy(false);
              }
            }}
            sx={{ mt: 5, maxWidth: 380 }}
          >
            {error && (
              <Alert severity="error" sx={{ mb: 2.5 }}>
                {error}
              </Alert>
            )}
            <Stack spacing={2}>
              <TextField
                required
                autoFocus
                label="账号"
                autoComplete="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#fff' } }}
              />
              <TextField
                required
                label="密码"
                type={reveal ? 'text' : 'password'}
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#fff' } }}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          aria-label={reveal ? '隐藏密码' : '显示密码'}
                          onClick={() => setReveal((v) => !v)}
                          edge="end"
                        >
                          {reveal ? (
                            <VisibilityOffOutlined fontSize="small" />
                          ) : (
                            <VisibilityOutlined fontSize="small" />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
              />
              <Button
                type="submit"
                size="large"
                variant="contained"
                disabled={busy}
                endIcon={<ArrowForward className="login-arrow" />}
                sx={{ py: 1.3, fontSize: 15 }}
              >
                {busy ? '正在登录…' : '登录工作台'}
              </Button>
            </Stack>
            <Typography color="text.secondary" sx={{ fontSize: 12.5, mt: 2.5 }}>
              忘记密码？请联系企业管理员重置。
            </Typography>
          </Box>
        </div>

        <div className="login-footnote">企业数据各自独立，仅与合作方共享已授权的交易信息。</div>
      </section>

      <section className="login-showcase rise" style={{ ['--d' as string]: '200ms' }}>
        <ProductStage />
      </section>
    </div>
  );
}
